import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

import { useTranslation } from 'next-i18next'

import Container from 'components/Container'
import InfoBox from 'components/InfoBox'
import LanguageMenu from 'components/LanguageDropdown'
import Modal from 'components/Modal'
import { LogoImage } from 'components/SectionHeader'
import { useBooking } from 'hooks/use-booking'
import { useBuilding } from 'hooks/use-building'
import { useGuest } from 'hooks/use-guest'
import Image from 'next/image'
import Link from 'next/link'
import posthog from 'posthog-js'
import { useState } from 'react'
import ReservationHasAccount from '../../components/ReservationHasAccount'
import { HomeProps } from 'types/pages/home'
import LoadingMessages from 'components/LoadingMessages'
import ReservationOptions from 'components/ReservationOptions'

const LoginPage = ({ reservationId }: HomeProps) => {
  const { t } = useTranslation(['login', 'common'])
  const [reservation, setReservation] = useState<string>(reservationId)
  const [isOpenModalHasAccount, setIsOpenModalHasAccount] =
    useState<boolean>(false)
  const [isActive, setIsActive] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const { saveBooking } = useBooking()
  const { saveBuilding } = useBuilding()
  const { saveGuest } = useGuest()

  const handlerGetReservation = async (checkinId: string) => {
    const checkin = await fetch(`/api/checkin/getReservation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        reserva: checkinId
      })
    }).then((response) => response.json())

    if (!checkin.data) {
      setMessage(checkin.status)
      setIsActive(false)
    } else {
      saveGuest(checkin.data.guest)
      saveBooking(checkin.data.booking)
      saveBuilding(checkin.data.building)
      setIsActive(true)
    }
    setLoading(false)
  }

  return (
    <div className="bg-[#1D1C1B] h-full min-h-screen">
      <main>
        <section className="py-6 bg-black">
          <Container className="flex justify-between">
            <LogoImage />
            <LanguageMenu />
          </Container>
        </section>

        <section className="w-full mt-20">
          <Modal
            open={isOpenModalHasAccount}
            setIsOpen={setIsOpenModalHasAccount}
          >
            <ReservationHasAccount reservation={reservation} />
          </Modal>

          <Container className="bg-white p-6 rounded-lg">
            <h1 className="font-serif text-[2rem] text-center mb-6 leading-10 font-semibold">
              {t('login-title')}
            </h1>
            {loading ? (
              <div className="justify-center align-center flex flex-col py-3 w-xl mx-auto">
                <Image
                  src={'/img/loading.gif'}
                  alt="Loading"
                  className="object-contain"
                  width={207}
                  height={207}
                />
                <LoadingMessages />
              </div>
            ) : (
              <div>
                {message && (
                  <div className="py-6">
                    <InfoBox message={t(message)} type={'warning'} />
                  </div>
                )}
                {isActive && !message ? (
                  <ReservationOptions
                    setLoading={setLoading}
                    setIsActive={setIsActive}
                    reservationCode={reservation}
                  />
                ) : (
                  <Formik
                    initialValues={{
                      reserva: reservation
                    }}
                    validationSchema={Yup.object({
                      reserva: Yup.string().required(t('errors.login-required'))
                    })}
                    onSubmit={async (values) => {
                      posthog.capture('click-in-without-account')
                      setLoading(true)
                      setReservation(values.reserva)

                      const isCheckinAccountLinked = await fetch(
                        `/api/cerberus/verifyCheckinAccountLinked?reservation=${values.reserva}`,
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            reserva: values.reserva
                          })
                        }
                      ).then((response) => response.json())

                      if (isCheckinAccountLinked) {
                        setLoading(false)
                        return setIsOpenModalHasAccount(true)
                      }

                      await handlerGetReservation(values.reserva)
                    }}
                  >
                    {({ errors, values }) => (
                      <Form>
                        <p
                          className="text-neutral-900 pb-2 login-label"
                          dangerouslySetInnerHTML={{
                            __html: t('login-description')
                          }}
                        />
                        <Field
                          id="reserva"
                          name="reserva"
                          placeholder={t('login-placeholder')}
                          className={`rounded form-input ${
                            !!errors.reserva && 'form-input--error'
                          }`}
                          value={values.reserva}
                        />
                        <ErrorMessage
                          name="reserva"
                          component="span"
                          className="error-message"
                        />

                        <p
                          className="mt-4 text-sm"
                          dangerouslySetInnerHTML={{
                            __html: t('login-info')
                          }}
                        />

                        <div className="flex gap-6 flex-col">
                          <Link
                            passHref
                            href={
                              `${process.env.NEXT_PUBLIC_SITE_URL}?openLogin=true` +
                              `${
                                values.reserva && `&reserva=${values.reserva}`
                              }`
                            }
                          >
                            <button
                              className="mt-4 button--dark w-full"
                              onClick={() =>
                                posthog.capture('click-in-with-account')
                              }
                            >
                              {t('button.with-account')}
                            </button>
                          </Link>

                          <button
                            type="submit"
                            className="underline w-full text-black mb-2"
                          >
                            {t('button.no-account')}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            )}
          </Container>
        </section>
      </main>
    </div>
  )
}

export default LoginPage
